import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InboxOutlined from '@mui/icons-material/InboxOutlined';

/**
 * EmptyState — Enterprise Design System
 *
 * Displays a centered placeholder when content is empty.
 * Premium look with gradient icon background, helpful message, and CTA.
 *
 * Props:
 *  - icon: MUI icon component (default: InboxOutlined)
 *  - title: string (default: "Nothing here yet")
 *  - description: string
 *  - action: ReactNode rendered below description
 */
function EmptyState({ icon, title, description, action }) {
  const Icon = icon || InboxOutlined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
      px={3}
      textAlign="center"
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Icon sx={{ fontSize: 32, color: '#64748b' }} />
      </Box>
      <Typography variant="h6" fontWeight={700} color="text.primary" mb={0.5} sx={{ fontSize: '1.1rem' }}>
        {title || 'Nothing here yet'}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          mb={action ? 3 : 0}
          maxWidth={360}
          sx={{ lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

export default EmptyState;
